import * as React from 'react';
import { MandateBillDto } from 'ts-types/api.types';
import styled from 'styled-components';
import { Accordion, MessageProps, Table } from 'semantic-ui-react';
import { withMessages } from 'react-message-source';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import { FieldArray } from 'react-final-form-arrays';
import { mustBeNumber } from 'utils/validatorUtils';
import { Field } from 'react-final-form';
import Input from 'components/final-form/Input';
import EditActionButton from 'components/EditActionButton';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';

const StyledField = styled(Field)`
  width: 70px;
`;

interface Props extends MessageProps {
  bills: MandateBillDto[],
  setEditBill: (editBill: boolean) => void
}

const StyledTableCell = styled(Table.Cell)`
 height: 47px;
`;

interface State {
  activeIndex: number,
  editBill: boolean,
  editBillIndex?: number,
  billCopy?: Partial<MandateBillDto>
}

const documentTypeMessages = ["bill", "firstReminder", "secondReminder", "thirdReminder"];

class BillInfoSection extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      activeIndex: -1,
      editBill: false,
      editBillIndex: -1
    };
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {activeIndex, editBill, editBillIndex} = this.state;

    return (
        <Accordion fluid>
          <Accordion.Title
              active={activeIndex === 0}
              index={0}
              onClick={this.toggleAccordion}
          >
            <h3><Icon name='dropdown'/>{getMessage("mandate.billInfo")}</h3>
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 0}>
              <Table celled selectable size='small'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={2}>{getMessage('mandate.documentType')}</Table.HeaderCell>
                    <Table.HeaderCell width={2}>{getMessage('mandate.paymentPeriod')}</Table.HeaderCell>
                    <Table.HeaderCell width={2}>{getMessage('mandate.gracePeriod')}</Table.HeaderCell>
                    <Table.HeaderCell width={2}>{getMessage('mandate.surcharge')}</Table.HeaderCell>
                    <Table.HeaderCell width={2}>{getMessage('mandate.numberOfCopies')}</Table.HeaderCell>
                    <Table.HeaderCell width={2}>{getMessage('actions')}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <FieldArray name="bills">
                    {({fields}) =>
                        fields.map((field, index) => {
                          const bill: MandateBillDto = fields.value[index];

                          if (editBill && editBillIndex === index) {

                            return (
                                <Table.Row key={index}>
                                  <Table.Cell width={2}>
                                    {getMessage(`mandate.bills.${documentTypeMessages[bill.documentTypeId]}`)}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <StyledField
                                        name={`${field}.period`}
                                        component={Input}
                                        validate={mustBeNumber}
                                        size='mini'
                                    />
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <StyledField
                                        name={`${field}.gracePeriod`}
                                        component={Input}
                                        validate={mustBeNumber}
                                        size='mini'
                                    />
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <StyledField
                                        name={`${field}.surcharge`}
                                        component={Input}
                                        validate={mustBeNumber}
                                        size='mini'
                                    />
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <StyledField
                                        name={`${field}.numcopies`}
                                        component={Input}
                                        validate={mustBeNumber}
                                        size='mini'
                                    />
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <SaveActionButton hoverMessage={getMessage('button.save')}
                                                      onConfirm={() => this.saveBill(fields)}/>
                                    <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                        onConfirm={() => this.cancelEditBill(fields, index)}/>
                                  </Table.Cell>
                                </Table.Row>
                            )
                          } else {
                            return (
                                <Table.Row key={index}>
                                  <Table.Cell width={2}>
                                    {getMessage(`mandate.bills.${documentTypeMessages[bill.documentTypeId]}`)}
                                  </Table.Cell>
                                  <StyledTableCell width={2}>
                                    {bill.period}
                                  </StyledTableCell>
                                  <Table.Cell width={2}>
                                    {bill.gracePeriod}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {bill.surcharge}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {bill.numcopies}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <EditActionButton
                                        hoverMessage={getMessage('msgEdit')}
                                        onConfirm={() => this.editBill(index, fields, bill)}
                                    />
                                    <DeleteActionButton
                                        hoverMessage={getMessage('msgDelete')}
                                        onConfirm={() => this.deleteBill(fields, index)}
                                    />
                                  </Table.Cell>
                                </Table.Row>
                            )
                          }
                        })
                    }
                  </FieldArray>
                </Table.Body>
              </Table>
          </Accordion.Content>
        </Accordion>
    );
  }

  toggleAccordion = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndex, editBill} = this.state;

    if (!editBill) {
      const newIndex = activeIndex === index ? -1 : index;
      this.setState({activeIndex: newIndex});
    }
  };

  saveBill = (fields: any) => {
    const {setEditBill} = this.props;

    if (fields.valid) {
      this.setState({
        editBill: false,
        editBillIndex: undefined,
        billCopy: undefined
      });
      setEditBill(false);
    }
  };

  editBill = (index: number, fields: any, bill: MandateBillDto): void => {
    const {billCopy, editBillIndex} = this.state;
    const {setEditBill} = this.props;

    if (billCopy) {
      fields.remove(editBillIndex);
      fields.insert(editBillIndex, billCopy);
    }

    this.setState({
      editBill: true,
      editBillIndex: index,
      billCopy: bill
    });

    setEditBill(true);
  };

  cancelEditBill = (fields: any, index: number) => {
    const {billCopy} = this.state;
    const {setEditBill} = this.props;


    fields.remove(index);
    fields.insert(index, billCopy);

    this.setState({
      editBill: false,
      editBillIndex: undefined,
      billCopy: undefined
    });

    setEditBill(false);
  };

  deleteBill = (fields: any, index: number) => {
    const bill = {
      ...fields.value[index],
      period: null,
      gracePeriod: null,
      surcharge: null,
      numcopies: null
    };

    fields.remove(index);
    fields.insert(index, bill);
  }

}

export default withMessages(BillInfoSection);
